/**
 * The just-in-time PO page's lines (BLU-1268, plan 90 section 5.4), as pure
 * functions so the rules are testable without rendering anything.
 *
 * A row is one blank: the union of what the batch PLANNED (the outcomes read,
 * one per linked blank) and what the PO holds (its lines). Staff fix the PO to
 * what they bought: a quantity per row (0 = not bought, which deletes the line
 * on save), a unit price per row, and blanks they bought that were not planned.
 *
 * Edits are RAW TEXT (DESIGN.md section 12.1): a parsed-number map would
 * silently revert a half-typed entry through the controlled input. Saving is
 * ONE `updateDraft` call: the whole quantity map (the service deletes a line
 * that is absent or 0) plus only the prices that changed.
 */
import type { JitOutcomes } from '@threadheads/shared';
import type { PurchaseOrder, PurchaseOrderItem } from '@/features/purchase-orders';
import { blankStyleName } from '@/lib/blank-style-name';
import { normalizeNumericText } from '@/lib/parse-kg';

/** updateDraft's bounds (overridePurchaseOrderItemPriceSchema). */
export const JIT_MAX_UNIT_PRICE = 99_999_999.99;
export const JIT_MAX_QTY = 999_999;

export interface AddedJitLine {
  skuId: string;
  sku: string | null;
  title: string | null;
  colour: string | null;
  size: string | null;
}

export interface JitLineOutcomeCounts {
  shipped: number;
  inProgress: number;
  cancelled: number;
  removed: number;
}

export interface JitLineRow {
  skuId: string;
  sku: string | null;
  title: string | null;
  colour: string | null;
  size: string | null;
  /** Units the batch planned; null while the outcomes are unknown. */
  planned: number | null;
  /** The PO line's quantity; null = no line ("Not bought"). */
  saved: number | null;
  /** The PO line's unit price (override, else the catalogue cost), PO currency. */
  unitPrice: number | null;
  /** What happened to the orders behind it; null while unknown. */
  outcome: JitLineOutcomeCounts | null;
  /** Picked on this page and not saved yet. */
  added: boolean;
  /** BLU-1501: the supplier's live stock, from the outcomes read. Absent or null = none known; 0 is real (read with `typeof`). */
  supplierStock?: number | null;
  /** BLU-1501: the supplier's next delivery day (YYYY-MM-DD), on the same terms. */
  nextDeliveryEta?: string | null;
}

export interface JitDraft {
  /** Raw text per SKU id, only for rows the operator touched. */
  qty: Record<string, string>;
  price: Record<string, string>;
  added: AddedJitLine[];
  /** A supplier picked for a "No supplier" order; undefined = unchanged. */
  supplier?: { id: string; name: string };
}

export const EMPTY_JIT_DRAFT: JitDraft = { qty: {}, price: {}, added: [] };

const itemPrice = (item: PurchaseOrderItem): number | null =>
  item.overridden_unit_price ?? item.unit_cost ?? null;

/**
 * Rows in the batch's order (the outcomes), then PO lines the batch did not
 * plan, then blanks added on this page. Outcomes unknown (loading or failed):
 * the PO lines alone, with no planned figure.
 */
export function buildJitRows(
  items: ReadonlyArray<PurchaseOrderItem>,
  outcomes: JitOutcomes | null | undefined,
  added: ReadonlyArray<AddedJitLine> = [],
): JitLineRow[] {
  const itemBySku = new Map<string, PurchaseOrderItem>();
  for (const item of items) {
    if (!item.sku_id) continue;
    const existing = itemBySku.get(item.sku_id);
    // One line per (PO, blank) is how sync writes them; sum defensively.
    itemBySku.set(
      item.sku_id,
      existing ? { ...existing, ordered_quantity: existing.ordered_quantity + item.ordered_quantity } : item,
    );
  }

  const rows: JitLineRow[] = [];
  const seen = new Set<string>();

  for (const line of outcomes?.lines ?? []) {
    if (!line.skuId || seen.has(line.skuId)) continue;
    seen.add(line.skuId);
    const item = itemBySku.get(line.skuId);
    rows.push({
      skuId: line.skuId,
      sku: line.sku ?? item?.sku ?? null,
      title: line.title ?? item?.title ?? null,
      colour: line.colour ?? item?.color ?? null,
      size: line.size ?? item?.size ?? null,
      planned: line.planned,
      saved: item ? item.ordered_quantity : null,
      unitPrice: item ? itemPrice(item) : null,
      outcome: {
        shipped: line.shipped,
        inProgress: line.inProgress,
        cancelled: line.cancelled,
        removed: line.removed,
      },
      added: false,
      supplierStock: line.supplierStock,
      nextDeliveryEta: line.nextDeliveryEta,
    });
  }

  const extra = [...itemBySku.values()]
    .filter((item) => !seen.has(item.sku_id as string))
    .sort((a, b) => (a.sku ?? '').localeCompare(b.sku ?? ''));
  for (const item of extra) {
    const skuId = item.sku_id as string;
    seen.add(skuId);
    rows.push({
      skuId,
      sku: item.sku ?? null,
      title: item.title ?? null,
      colour: item.color ?? null,
      size: item.size ?? null,
      planned: outcomes ? 0 : null,
      saved: item.ordered_quantity,
      unitPrice: itemPrice(item),
      outcome: outcomes ? { shipped: 0, inProgress: 0, cancelled: 0, removed: 0 } : null,
      added: false,
    });
  }

  for (const line of added) {
    if (seen.has(line.skuId)) continue;
    seen.add(line.skuId);
    rows.push({
      ...line,
      planned: outcomes ? 0 : null,
      saved: null,
      unitPrice: null,
      outcome: null,
      added: true,
    });
  }
  return rows;
}

// ── Parsing ──────────────────────────────────────────────────────────────────

export type QtyParse = { kind: 'ok'; value: number } | { kind: 'invalid' };

/** A whole number of units. Empty reads as 0 ("Not bought"). */
export function parseBoughtQty(text: string): QtyParse {
  const s = normalizeNumericText(text);
  if (s === '') return { kind: 'ok', value: 0 };
  if (!/^\d+$/.test(s)) return { kind: 'invalid' };
  const value = Number(s);
  return value <= JIT_MAX_QTY ? { kind: 'ok', value } : { kind: 'invalid' };
}

export type PriceParse =
  | { kind: 'blank' }
  | { kind: 'ok'; value: number }
  | { kind: 'invalid' }
  | { kind: 'tooPrecise' };

/** A unit price above 0 with at most 2 decimals. Empty = keep the line's price. */
export function parseUnitPrice(text: string): PriceParse {
  const s = normalizeNumericText(text).replace(/,/g, '');
  if (s === '') return { kind: 'blank' };
  if (!/^\d*\.?\d*$/.test(s) || s === '.') return { kind: 'invalid' };
  const value = Number(s);
  if (!Number.isFinite(value) || value <= 0 || value > JIT_MAX_UNIT_PRICE) return { kind: 'invalid' };
  // The backend stores cents. Refuse more instead of silently rounding what was typed.
  if ((s.split('.')[1] ?? '').replace(/0+$/, '').length > 2) return { kind: 'tooPrecise' };
  return { kind: 'ok', value };
}

// ── Row state ────────────────────────────────────────────────────────────────

export const rowQtyText = (row: JitLineRow, draft: JitDraft): string =>
  draft.qty[row.skuId] ?? (row.saved !== null ? String(row.saved) : '');

/**
 * A saved price as the box shows it: 2 decimals ("8.50", never "8.5"). A price
 * with more decimals than that (a catalogue cost) shows whole, so the box never
 * reads a different number than the line holds.
 */
export function priceText(value: number): string {
  const fixed = value.toFixed(2);
  return Number(fixed) === value ? fixed : String(value);
}

export const rowPriceText = (row: JitLineRow, draft: JitDraft): string =>
  draft.price[row.skuId] ?? (row.unitPrice !== null ? priceText(row.unitPrice) : '');

/** The quantity the row will hold after Save; null while its text is invalid. */
export function rowQty(row: JitLineRow, draft: JitDraft): number | null {
  const parsed = parseBoughtQty(rowQtyText(row, draft));
  return parsed.kind === 'ok' ? parsed.value : null;
}

export type RowPriceState =
  | { kind: 'ok'; value: number | null }
  | { kind: 'invalid' }
  | { kind: 'tooPrecise' };

/** The unit price after Save (null = none on the line); invalid text says why. */
export function rowPrice(row: JitLineRow, draft: JitDraft): RowPriceState {
  const text = draft.price[row.skuId];
  if (text === undefined) return { kind: 'ok', value: row.unitPrice };
  const parsed = parseUnitPrice(text);
  if (parsed.kind === 'blank') return { kind: 'ok', value: row.unitPrice };
  if (parsed.kind === 'ok') return { kind: 'ok', value: parsed.value };
  return parsed;
}

const priceChanged = (row: JitLineRow, draft: JitDraft): boolean => {
  const price = rowPrice(row, draft);
  return price.kind === 'ok' && price.value !== null && price.value !== row.unitPrice;
};

/** Units the batch still needs from this row: shipped plus in progress. */
export const usedUnits = (outcome: JitLineOutcomeCounts): number => outcome.shipped + outcome.inProgress;

/** Bought beyond what the batch's orders still use (a spare); 0 when unknown. */
export function leftoverUnits(row: JitLineRow, bought: number): number {
  if (!row.outcome || bought <= 0) return 0;
  return Math.max(0, bought - usedUnits(row.outcome));
}

export interface JitDraftSummary {
  /** Anything edited (lines, prices, the supplier, or text still to fix). */
  dirty: boolean;
  /** The quantity map or a price must be sent. */
  linesChanged: boolean;
  /** Rows whose text cannot be saved. */
  invalidCount: number;
  /** Units after Save (invalid rows count as 0). */
  totalUnits: number;
  /** Cost after Save in PO currency; rows with no price add nothing. */
  totalCost: number;
  /** Bought rows with no price. */
  missingPriceCount: number;
}

export function summarizeJitDraft(rows: ReadonlyArray<JitLineRow>, draft: JitDraft): JitDraftSummary {
  let linesChanged = false;
  let invalidCount = 0;
  let totalUnits = 0;
  let totalCost = 0;
  let missingPriceCount = 0;
  for (const row of rows) {
    const qty = rowQty(row, draft);
    const price = rowPrice(row, draft);
    if (qty === null || price.kind !== 'ok') invalidCount += 1;
    if (row.added) linesChanged = true;
    if (qty !== null && qty !== (row.saved ?? 0)) linesChanged = true;
    if ((qty ?? 0) > 0 && priceChanged(row, draft)) linesChanged = true;
    const units = qty ?? 0;
    totalUnits += units;
    if (units > 0) {
      if (price.kind === 'ok' && price.value !== null) totalCost += price.value * units;
      else if (price.kind === 'ok') missingPriceCount += 1;
    }
  }
  return {
    // Text that cannot be saved is still an edit: Save stays on screen (disabled,
    // with the count to fix) and Discard can take it back.
    dirty: linesChanged || draft.supplier !== undefined || invalidCount > 0,
    linesChanged,
    invalidCount,
    totalUnits,
    totalCost: Math.round(totalCost * 100) / 100,
    missingPriceCount,
  };
}

/**
 * "Bought as listed" is honest only when every row reads bought = planned: no
 * quantity changed, nothing added, nothing taken off. Mirrors the backend's
 * `isBoughtAsListed`, which re-checks it. Unknown outcomes: never as listed.
 */
export function isBoughtAsListed(rows: ReadonlyArray<JitLineRow>): boolean {
  if (rows.length === 0 || rows.some((row) => row.planned === null || row.added)) return false;
  return rows.every((row) => row.saved !== null && row.saved === row.planned);
}

export interface JitSavePayload {
  orderId: string;
  locationId: string;
  supplierId?: string;
  supplierName?: string;
  orderQuantities?: Record<string, number>;
  unitCosts?: Record<string, number>;
}

/**
 * The one `updateDraft` input for the draft, or null when nothing can (or
 * needs to) be saved. The quantity map carries EVERY line the PO holds (0
 * deletes it) plus new rows above 0, because the service treats an absent
 * line as deleted.
 */
export function buildJitSavePayload(
  order: Pick<PurchaseOrder, 'id' | 'location_id'>,
  rows: ReadonlyArray<JitLineRow>,
  draft: JitDraft,
): JitSavePayload | null {
  if (!order.location_id) return null;
  const summary = summarizeJitDraft(rows, draft);
  if (!summary.dirty || summary.invalidCount > 0) return null;

  const payload: JitSavePayload = { orderId: order.id, locationId: order.location_id };
  if (draft.supplier) {
    payload.supplierId = draft.supplier.id;
    payload.supplierName = draft.supplier.name;
  }
  if (summary.linesChanged) {
    if (summary.totalUnits <= 0) return null;
    const orderQuantities: Record<string, number> = {};
    const unitCosts: Record<string, number> = {};
    for (const row of rows) {
      const qty = rowQty(row, draft) ?? 0;
      if (row.saved !== null || qty > 0) orderQuantities[row.skuId] = qty;
      const price = rowPrice(row, draft);
      if (qty > 0 && priceChanged(row, draft) && price.kind === 'ok' && price.value !== null) {
        unitCosts[row.skuId] = price.value;
      }
    }
    payload.orderQuantities = orderQuantities;
    if (Object.keys(unitCosts).length > 0) payload.unitCosts = unitCosts;
  }
  return payload;
}

/**
 * "Staple Tee · Black · M", the words a row is named by (labels, errors). The
 * title is the SKU's, which for a blank already ends in its size and colour
 * ("Staple Tee M Black"), so those come off it first and each is said once.
 */
export function jitRowName(row: Pick<JitLineRow, 'title' | 'colour' | 'size' | 'sku'>): string {
  const title = row.title?.trim() ? blankStyleName(row.title, row.colour, row.size) : null;
  const parts = [title, row.colour, row.size].filter((part): part is string => Boolean(part?.trim()));
  return parts.length > 0 ? parts.join(' · ') : row.sku ?? '';
}
